import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/sonner'
import { AdminAuthProvider } from '@/contexts/AdminAuthContext'
import { AdminProtectedRoute } from '@/components/auth/AdminProtectedRoute'
import { AdminLoginPage } from '@/components/auth/AdminLoginPage'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DashboardPage } from '@/components/dashboard/DashboardPage'
import { UserListPage } from '@/components/users/UserListPage'
import { UserDetailPage } from '@/components/users/UserDetailPage'
import { CatalogListPage } from '@/components/catalog/CatalogListPage'
import { CatalogItemEditor } from '@/components/catalog/CatalogItemEditor'
import { SubscriptionListPage } from '@/components/subscriptions/SubscriptionListPage'
import { AuditLogPage } from '@/components/support/AuditLogPage'
import { FeedbackListPage } from '@/components/feedback/FeedbackListPage'
import { FeedbackThreadPage } from '@/components/feedback/FeedbackThreadPage'
import { QaFeedbackListPage } from '@/components/feedback/QaFeedbackListPage'
import { RevenueDashboardPage } from '@/components/revenue/RevenueDashboardPage'
import { SystemSettingsPage } from '@/components/settings/SystemSettingsPage'
import { LayoutBrowserPage } from '@/components/layouts/LayoutBrowserPage'
import { LayoutDetailPage } from '@/components/layouts/LayoutDetailPage'
import { LocationsListPage } from '@/components/locations/LocationsListPage'
import { LocationDetailPage } from '@/components/locations/LocationDetailPage'
import { GiftLicensesPage } from '@/components/users/GiftLicensesPage'
import { SystemHealthPage } from '@/components/system/SystemHealthPage'
import { OpsReviewPage } from '@/components/ops/OpsReviewPage'
import { EmailLogPage } from '@/components/system/EmailLogPage'
import { DisputesPage } from '@/components/billing/DisputesPage'
import { NotificationsPage } from '@/components/notifications/NotificationsPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<AdminLoginPage />} />

            <Route element={<AdminProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="users" element={<UserListPage />} />
                <Route path="users/:userId" element={<UserDetailPage />} />
                <Route path="catalog" element={<CatalogListPage />} />
                <Route path="catalog/new" element={<CatalogItemEditor />} />
                <Route path="catalog/:itemId" element={<CatalogItemEditor />} />
                <Route path="subscriptions" element={<SubscriptionListPage />} />
                <Route path="feedback" element={<FeedbackListPage />} />
                <Route path="feedback/:conversationId" element={<FeedbackThreadPage />} />
                <Route path="qa-feedback" element={<QaFeedbackListPage />} />
                <Route path="revenue" element={<RevenueDashboardPage />} />
                <Route path="locations" element={<LocationsListPage />} />
                <Route path="locations/:locationId" element={<LocationDetailPage />} />
                <Route path="layouts" element={<LayoutBrowserPage />} />
                <Route path="layouts/:layoutId" element={<LayoutDetailPage />} />
                <Route path="gift-licenses" element={<GiftLicensesPage />} />
                <Route path="health" element={<SystemHealthPage />} />
                <Route path="ops-reviews" element={<OpsReviewPage />} />
                <Route path="emails" element={<EmailLogPage />} />
                <Route path="billing" element={<DisputesPage />} />
                <Route path="settings" element={<SystemSettingsPage />} />
                <Route path="audit" element={<AuditLogPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AdminAuthProvider>
    </QueryClientProvider>
  )
}
